import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../../AuthContext";
import { useLocation } from "react-router-dom";
import "./Imagedisplay.css";
import { apiUrl } from "../../config/apiBase";

// Backend mounts image routes at /api/images (see index.js)
const JOB_IMAGE_BASE = apiUrl('/api/images/job');

const Imagedisplay = ({ jobId: propJobId, imageUrl: directImageUrl }) => {
  const location = useLocation();
  const { refreshTokenIfNeeded } = useAuth();
  const params = new URLSearchParams(location.search);
  const urlJobId = params.get("job_id");
  
  // Use prop jobId if provided, otherwise use from URL
  const jobId = propJobId || urlJobId;

  const [image, setImage] = useState(directImageUrl ? { image_url: directImageUrl } : null);
  const [loading, setLoading] = useState(!directImageUrl);
  const [fetchError, setFetchError] = useState("");
  const [imgLoading, setImgLoading] = useState(!!directImageUrl);
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null); // holds timeout id (not interval now)
  const abortRef = useRef(null);
  
  // Dispatch event whenever image changes and is valid
  useEffect(() => {
    if (image) {
      // Dispatching imageLoaded event
      // Create a consistent object structure even if some fields are missing
      const imageEventData = {
        id: image.id || `temp-${Date.now()}`,
        image_url: image.image_url,
        job_title: image.job_title || 'Software Engineer',
        company: image.company || 'Tech Company',
        job_id: image.job_id || jobId,
        questions: image.questions || []
      };
      
      const event = new CustomEvent('imageLoaded', { 
        detail: { imageData: imageEventData } 
      });
      window.dispatchEvent(event);
      window.currentImage = imageEventData;
    }
  }, [image, jobId]);
  
  // Reset state when jobId changes
  useEffect(() => {
    // jobId changed
    setImage(directImageUrl ? { image_url: directImageUrl } : null);
    setLoading(!directImageUrl);
    setFetchError("");
    setImgLoading(!!directImageUrl);
    setAttempts(0);
  }, [jobId, directImageUrl]);

  const fetchImage = useCallback(async () => {
    // If we already have a direct image URL, don't fetch
    if (directImageUrl || !jobId) return;
    
    // cancel any previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Fetching image
      const token = await refreshTokenIfNeeded();
      const resp = await fetch(`${JOB_IMAGE_BASE}/${jobId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal
      });
      if (resp.status === 404) {
        const errorData = await resp.json().catch(() => ({}));
        
        // Check if this is an image recovery scenario (expired URL being regenerated)
        if (errorData.message && errorData.message.includes('recovery')) {
          console.log('Imagedisplay: Image recovery in progress, will retry');
          setFetchError('Regenerating image... This will only take a moment.');
        } else {
          console.log('Imagedisplay: Image not ready yet (404), will retry');
        }
        
        setAttempts(a => a + 1);
        return; // still generating or recovering
      }
      if (!resp.ok) throw new Error(`Image fetch failed (${resp.status})`);
      const data = await resp.json();
      setImage(data);
      setImgLoading(true);
      
      // Clear any error message on success
      setFetchError('');
      
      // stop scheduling further polls
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      if (e.name === 'AbortError') return; // ignore cancelled
      console.error('Imagedisplay: Image fetch error:', e);
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, directImageUrl, refreshTokenIfNeeded]);

  // Seed from sessionStorage if available (immediate image from job creation)
  useEffect(() => {
    if (directImageUrl || !jobId || image) return;
    
    try {
      const cached = sessionStorage.getItem('pendingImage');
      if (cached) {
        const parsed = JSON.parse(cached);
          if (parsed?.job_id === jobId) {
            setImage(parsed);
            setImgLoading(true);
            window.currentImage = parsed;
          }
      }
    } catch { /* ignore */ }
  }, [jobId, image, directImageUrl]);

  useEffect(() => {
    // Skip polling if direct image URL provided or if we already have an image
    if (directImageUrl || !jobId || image) return;
    
    // Define max attempts
    const MAX_ATTEMPTS = 30; // Increased from 25 to allow more time
    const scheduleNext = () => {
      if (image) return; // already have image
      if (attempts >= MAX_ATTEMPTS) {
        if (pollRef.current) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
        }
        return;
      }
      // ⚡ PERFORMANCE: Faster initial polling - 1s for first 3 attempts, then exponential backoff
      // This makes the UI feel more responsive when images are ready quickly
      let delay;
      if (attempts < 3) {
        delay = 1000; // Fast polling for first 3 attempts (1s)
      } else if (attempts < 10) {
        delay = 2000; // Medium polling (2s)
      } else {
        delay = Math.min(8000, 2000 + attempts * 400); // Slower polling, max 8s
      }
      pollRef.current = setTimeout(() => {
        fetchImage();
      }, delay);
    };
    // initial fetch (attempts starts at 0)
    fetchImage();
    scheduleNext();
    return () => {
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
      if (abortRef.current) abortRef.current.abort();
    };
  }, [jobId, image, attempts, fetchImage, directImageUrl]);

  if (loading && !image) {
    return <div className="image-loading">Loading technical scenario image...</div>;
  }

  if (fetchError) {
    return <div className="image-error">Failed to load image: {fetchError}</div>;
  }

  if (!image) {
    return <div className="image-missing">No image available for this scenario</div>;
  }

  return (
    <div className="image-display-container">
      <div className="image-canvas">
        {imgLoading && <div className="image-loading-overlay">Loading image...</div>}
        <img
          key={image.id || image.image_url}
          src={image.image_url}
          alt="Technical scenario"
          className="technical-scenario-image"
          style={{ display: imgLoading ? "none" : "block" }}
          onLoad={() => setImgLoading(false)}
          onError={() => {
            setFetchError("Image failed to load");
            setImgLoading(false);
          }}
        />
      </div>
    </div>
  );
};

export default Imagedisplay;